import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  TimeSlot,
  Service,
  Professional,
  Resource,
  ReservationRequest,
  Reservation
} from '../models/reservation.models';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class ReservationService {
  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private config: ConfigService
  ) {
    this.baseUrl = this.config.getApiUrl();
  }

  getBookableBusinesses(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/api/v1/reservations/businesses`
    );
  }

  getAvailability(businessId: string, date: string): Observable<TimeSlot[]> {
    return this.http.get<TimeSlot[]>(
      `${this.baseUrl}/api/v1/reservations/businesses/${businessId}/availability`,
      { params: { date } }
    );
  }

  getServices(businessId: string): Observable<Service[]> {
    return this.http.get<Service[]>(
      `${this.baseUrl}/api/v1/reservations/businesses/${businessId}/services`
    );
  }

  getProfessionals(businessId: string): Observable<Professional[]> {
    return this.http.get<Professional[]>(
      `${this.baseUrl}/api/v1/reservations/businesses/${businessId}/professionals`
    );
  }

  getResources(businessId: string): Observable<Resource[]> {
    return this.http.get<Resource[]>(
      `${this.baseUrl}/api/v1/reservations/businesses/${businessId}/resources`
    );
  }

  createReservation(request: ReservationRequest): Observable<Reservation> {
    return this.http.post<Reservation>(
      `${this.baseUrl}/api/v1/reservations`,
      request
    );
  }

  listReservations(businessId: string, filters?: { date?: string; status?: string }): Observable<Reservation[]> {
    let params = new HttpParams();
    if (filters?.date) {
      params = params.set('date', filters.date);
    }
    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    return this.http.get<Reservation[]>(
      `${this.baseUrl}/api/v1/reservations/businesses/${businessId}`,
      { params }
    );
  }

  cancelReservation(businessId: string, reservationId: number): Observable<Reservation> {
    return this.http.put<Reservation>(
      `${this.baseUrl}/api/v1/reservations/businesses/${businessId}/${reservationId}/cancel`,
      {}
    );
  }

  cancelMyReservation(reservationId: string): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      `${this.baseUrl}/api/v1/reservations/my/${reservationId}/cancel`,
      {}
    );
  }

  deleteMyReservation(reservationId: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${this.baseUrl}/api/v1/reservations/my/${reservationId}`
    );
  }

  getMyReservations(limit = 20, offset = 0): Observable<MyReservationsResponse> {
    return this.http.get<MyReservationsResponse>(
      `${this.baseUrl}/api/v1/reservations/my`,
      { params: { limit: limit.toString(), offset: offset.toString() } }
    );
  }
}

export interface MyReservationItem {
  id: string;
  business_id: string;
  business_name?: string;
  tpv_reservation_id?: number;
  status: string;
  business_type: string;
  customer_name: string;
  reservation_date: string;
  time_start: string;
  time_end?: string;
  party_size?: number;
  notes?: string;
  created_at: string;
  cancelled_at?: string;
}

export interface MyReservationsResponse {
  success: boolean;
  reservations: MyReservationItem[];
}
